import type { FullPageType } from '@/api/types/PageType';
import type { FullPage, User } from '@/types';

import { loadState } from '@/store/localStorage';
import { useQuery } from '@apollo/client';
import { useMemo } from 'react';

import apiClient from './apiClient';
import { GRAPHQL_SERVERS } from './graphqlClient';
import { devicesQuery, loggedInUserQuery } from './query';

// Devices Request Related

interface IQueryResult {
  data?: FullPage;
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
}

export function transformDevices(data: FullPageType): FullPage {
  return {
    uri: data.uri,
    slug: data.slug,
    template: data.template?.templateName,
    title: data.title,
    sections: data.sections?.components,
    seo: data.seo
      ? {
          title: data.seo.title,
          metaDesc: data.seo.metaDesc,
          twitterImage: data.seo.twitterImage
            ? {
                url: data.seo.twitterImage.node.sourceUrl,
                title: data.seo.twitterImage.node.title,
              }
            : undefined,
          twitterDescription: data.seo.twitterDescription,
          twitterTitle: data.seo.twitterTitle,
        }
      : undefined,
  };
}

export const useGetDevices = (): IQueryResult => {
  const { data, loading, error, refetch } = useQuery(devicesQuery, {
    context: {
      version: '1.0',
      server: GRAPHQL_SERVERS.WEBAPP,
    },
    fetchPolicy: 'no-cache',
  });

  // Post-process the data
  const cleanedData = useMemo(() => {
    if (!data) {
      return undefined;
    }

    return undefined;
  }, [data]);

  return { data: cleanedData, loading, error, refetch };
};

interface ILoggedInUserQueryResult {
  data?: User;
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
}

export const useGetLoggedInUser = (): ILoggedInUserQueryResult => {
  const hastToken = !!loadState('authToken');

  const { data, loading, error, refetch } = useQuery(loggedInUserQuery, {
    context: {
      version: '1.0',
      server: GRAPHQL_SERVERS.WEBAPP,
    },
    fetchPolicy: 'no-cache',
    skip: !hastToken,
  });

  const cleanedData = useMemo(() => {
    if (!data) {
      return undefined;
    }

    const { loggedInUser } = data;

    if (!loggedInUser) {
      return undefined;
    }

    const formattedData: User = {
      ...loggedInUser?.user.person,
      basket: loggedInUser?.basket,
    };

    return formattedData;
  }, [data]);

  return { data: cleanedData, loading, error, refetch };
};

export const logoutAuth = async (): Promise<void> => {
  try {
    await apiClient.post('/auth/logout');
  } catch {
    /* Logout anyway */
  }

  delete apiClient.defaults.headers.common['Authorization'];
};
